/**
 * Helper function to check if an error is an AbortError.
 * Handles various error types that can occur during HTTP requests.
 * @param error - The error to check (can be any type of error from HTTP requests, Promise.allSettled, etc.)
 * @returns true if the error is an AbortError, false otherwise
 */
export const isAbortError = (error: unknown): boolean => {
  if (!error) {
    return false;
  }

  // Direct AbortError check
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }

  // Check for HTTPError with AbortError cause
  if (error instanceof Error) {
    // Check if it's an HTTPError wrapping an AbortError
    if (
      'cause' in error &&
      error.cause instanceof DOMException &&
      error.cause.name === 'AbortError'
    ) {
      return true;
    }

    // Check if the error itself has AbortError name
    if (error.name === 'AbortError') {
      return true;
    }

    // Check if the error message indicates it's an abort error
    if (
      error.message?.includes('aborted') ||
      error.message?.includes('AbortError')
    ) {
      return true;
    }
  }

  // Check for plain objects with AbortError properties
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as Record<string, unknown>;

    // Check name property
    if (errorObj.name === 'AbortError') {
      return true;
    }

    // Check for nested cause
    if (
      errorObj.cause &&
      typeof errorObj.cause === 'object' &&
      errorObj.cause !== null
    ) {
      const cause = errorObj.cause as Record<string, unknown>;
      if (cause.name === 'AbortError') {
        return true;
      }
    }

    // Check for message indicating abort
    if (
      typeof errorObj.message === 'string' &&
      (errorObj.message.includes('aborted') ||
        errorObj.message.includes('AbortError'))
    ) {
      return true;
    }
  }

  return false;
};
